import { config } from '../config';
import { parsePlaceSuggestions, type PlaceSuggestion } from '../lib/work-location-form';
import { normalizeMyAttendance, type MyAttendance } from '../lib/my-attendance';
import type { SuratJalanErp } from '../lib/tarik-do';
import type { Member, WorkerProfile } from '../lib/worker-candidates';
import type { RoleAssignment, WorkforceRole } from '../lib/worker-roles';
import { parseTaskDocuments } from '../lib/task-documents';
import type { TerakhirTerlihat } from '../lib/last-seen';
import type { DocumentType, TaskDocument } from '../lib/task-documents';
import { ApiError, buildHeaders, attendanceRangeQuery, toApiError } from './request';
import type { AuthState } from './request';
import type {
  AttendanceActionResult,
  AttendanceCorrection,
  AttendanceDay,
  AttendanceEvent,
  AttendancePolicyResponse,
  AttendanceSummary,
  CheckInBody,
  CheckOutBody,
  CreateCorrectionBody,
  CreateFieldTaskBody,
  CreateSelfieUploadTargetBody,
  DecideCorrectionBody,
  DeliveryAttempt,
  DeliveryDetail,
  CompleteTaskBody,
  CreateUploadTargetBody,
  FailTaskBody,
  FailureReasonCode,
  SignedUrlResponse,
  TaskSummary,
  UploadTargetResponse,
  FieldTask,
  FieldTaskEvent,
  TaskLocationBody,
  TaskTypeResponse,
  KatalogPaketResponse,
  MeResponse,
  WorkLocationResponse,
  AssetResponse,
  CreateAssetBody,
  UpdateAssetBody,
  RemoteSessionResponse,
  OpenRemoteSessionBody,
  OpenedRemoteSessionResponse,
  TelemetryLatestMetric,
  TelemetryDailyPoint,
  AlertEventResponse,
  AlertRuleResponse,
  CreateAlertRuleBody,
  UpdateAlertRuleBody,
  AgentTokenView,
  IssuedAgentToken,
} from './types';

export { ApiError };
export type { AuthState };

/**
 * Klien API Workforce — hanya perekat antara `request.ts` (murni, teruji) dan `fetch`.
 *
 * Sengaja tidak ada logika keputusan di berkas ini. Semua yang layak diuji berada di
 * `request.ts`, karena berkas ini mengimpor `config` yang membaca `import.meta.env` milik Vite
 * dan tidak dapat dimuat di lingkungan pengujian Node.
 */
async function request<T>(
  path: string,
  auth: AuthState | null,
  init: { method?: string; body?: unknown; idempotencyKey?: string } = {},
): Promise<T> {
  const hasBody = init.body !== undefined;
  const headers = buildHeaders(auth, { hasBody, idempotencyKey: init.idempotencyKey });

  let response: Response;
  try {
    response = await fetch(`${config.apiBaseUrl}${path}`, {
      method: init.method ?? 'GET',
      headers,
      body: hasBody ? JSON.stringify(init.body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Tidak dapat menghubungi server.', null);
  }

  const correlationId = response.headers.get('x-correlation-id');
  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // Badan bukan JSON — `toApiError` sudah menyiapkan pesan bawaan.
    }
    throw toApiError(response.status, body, correlationId);
  }

  // `204 No Content` (mis. DELETE membatalkan unggahan) tidak punya badan untuk di-parse —
  // memaksa `response.json()` di jalur ini melempar SyntaxError pada respons yang justru sukses.
  if (response.status === 204) {
    return undefined as T;
  }

  const envelope = (await response.json()) as { data: T };
  return envelope.data;
}

export const fetchMe = (auth: AuthState): Promise<MeResponse> =>
  request<MeResponse>('/api/me', auth);

export const fetchActivePolicy = (auth: AuthState): Promise<AttendancePolicyResponse | null> =>
  request<AttendancePolicyResponse | null>('/api/attendance-policies/active', auth);

export const fetchWorkLocations = (auth: AuthState): Promise<readonly WorkLocationResponse[]> =>
  request<readonly WorkLocationResponse[]>('/api/work-locations', auth);

export const fetchKatalogPaket = (auth: AuthState): Promise<KatalogPaketResponse> =>
  request<KatalogPaketResponse>('/api/paket/katalog', auth);

// ── Installed base & remote (ADR-0054 Fase 1) ────────────────────────────────
export const fetchAssets = (
  auth: AuthState,
  provinsi?: string,
): Promise<readonly AssetResponse[]> =>
  request<readonly AssetResponse[]>(
    `/api/assets${provinsi ? `?provinsi=${encodeURIComponent(provinsi)}` : ''}`,
    auth,
  );

export const createAsset = (auth: AuthState, body: CreateAssetBody): Promise<AssetResponse> =>
  request<AssetResponse>('/api/assets', auth, { method: 'POST', body });

export const updateAsset = (
  auth: AuthState,
  id: string,
  body: UpdateAssetBody,
): Promise<AssetResponse> =>
  request<AssetResponse>(`/api/assets/${encodeURIComponent(id)}`, auth, { method: 'PATCH', body });

export const deleteAsset = (auth: AuthState, id: string): Promise<{ readonly ok: true }> =>
  request<{ readonly ok: true }>(`/api/assets/${encodeURIComponent(id)}`, auth, {
    method: 'DELETE',
  });

export const openRemoteSession = (
  auth: AuthState,
  body: OpenRemoteSessionBody,
): Promise<OpenedRemoteSessionResponse> =>
  request<OpenedRemoteSessionResponse>('/api/remote/sessions', auth, { method: 'POST', body });

export const fetchRemoteSessions = (
  auth: AuthState,
  assetId?: string,
): Promise<readonly RemoteSessionResponse[]> =>
  request<readonly RemoteSessionResponse[]>(
    `/api/remote/sessions${assetId ? `?assetId=${encodeURIComponent(assetId)}` : ''}`,
    auth,
  );

/**
 * Bootstrap peran Workforce pertama (QA-WF-003/BLD-017) — dipanggil `TrialStartPage.tsx` SEKALI,
 * tepat setelah organisasi dibuat lewat `/onboarding` milik HBS. Tanpa panggilan ini, pemilik
 * organisasi baru masuk ke Workforce TANPA peran apa pun ("Belum punya peran Workforce").
 */
export const bootstrapOnboarding = (auth: AuthState): Promise<{ readonly granted: boolean }> =>
  request<{ readonly granted: boolean }>('/api/onboarding', auth, { method: 'POST' });

/**
 * Bentuk yang dipakai layar. Server hari ini mengirim ARRAY telanjang; `normalizeMyAttendance`
 * yang menjembataninya. Lihat alasan lengkap dan akibatnya di `lib/my-attendance.ts`.
 */
export type MyAttendanceResponse = MyAttendance;

/**
 * Keadaan absensi diri sendiri HARI INI — jawaban SERVER, bukan kesimpulan klien.
 *
 * Menggantikan cara lama: mengambil rentang riwayat lalu menyimpulkan sesi terbuka di klien.
 * Cara itu lahir ketika endpoint ini belum ada, dan bertahan setelah ia ada — dengan dua akibat
 * yang keduanya terjadi di pilot 16 Agustus 2026:
 *
 *   1. Bentuk yang diminta tidak pernah dikirim. Klien mengharapkan `{ days, events }`;
 *      `GET /api/attendance/me` mengembalikan array `AttendanceDay` telanjang. `data.events`
 *      undefined, `.map()` melempar.
 *   2. Kegagalan itu ditelan `catch` yang menyetel mode ke CHECK_IN. Layar lalu menawarkan
 *      Check In dengan percaya diri kepada orang yang sesinya MASIH TERBUKA — dan penolakannya
 *      baru terlihat setelah tombol ditekan.
 *
 * Hari kerja, zona waktu, dan sesi terbuka lintas tengah malam kini ditentukan server memakai
 * kebijakan organisasi. Klien tidak lagi menghitung tanggal dari jam perangkat.
 */
export interface TodayAttendanceResponse {
  readonly workDate: string | null;
  readonly timezone: string | null;
  readonly policyActive: boolean;
  readonly openSessionStartedOnEarlierDay: boolean;
  /** `'check_in'` | `'check_out'`; `null` bila belum ada kebijakan aktif. */
  readonly nextAction: 'check_in' | 'check_out' | null;
  /**
   * Keadaan hari ini menurut SERVER. `null` bila projection hari itu belum terbentuk.
   *
   * Server sudah mengirimkannya sejak #472 (9 Agu 2026); tipe di sisi klien ini yang belum
   * menyebutnya, sehingga `firstCheckInAt` tidak terlihat oleh layar mana pun. Dideklarasikan
   * saat penjaga "absen pulang terlalu cepat" membutuhkannya — bukan penambahan API.
   */
  readonly today: {
    readonly status: string;
    readonly firstCheckInAt: string | null;
    readonly lastCheckOutAt: string | null;
    readonly workedMinutes: number;
    readonly lateMinutes: number;
  } | null;
}

export const fetchTodayAttendance = (auth: AuthState): Promise<TodayAttendanceResponse> =>
  request<TodayAttendanceResponse>('/api/me/attendance/today', auth);

/**
 * Riwayat absensi diri sendiri pada rentang tanggal. Dipakai layar Riwayat.
 */
/**
 * @param includeEvents minta server MENGIRIM event, bukan hanya hari.
 *
 * Bawaannya `false` dan itu disengaja: tiap baris event membawa `policy_snapshot` (seluruh
 * kebijakan yang berlaku saat itu), jadi ia berat. Layar "Ajukan Koreksi" meminta rentang satu
 * tahun kalender dan hanya memakai `days`; ia tidak boleh ikut membayar ratusan blob kebijakan
 * lewat kuota kurir. Hanya layar Riwayat yang benar-benar membaca event.
 */
export const fetchMyAttendance = (
  auth: AuthState,
  from: string,
  to: string,
  includeEvents = false,
): Promise<MyAttendanceResponse> =>
  request<unknown>(
    `/api/attendance/me?${attendanceRangeQuery(from, to)}${includeEvents ? '&includeEvents=true' : ''}`,
    auth,
  ).then(normalizeMyAttendance);

/**
 * Kehadiran SELURUH orang dalam cakupan pemanggil, pada rentang tanggal.
 *
 * Cakupannya ditentukan SERVER dari peran pemanggil — layar tidak mengirim daftar orang, dan
 * memaksa URL-nya tidak menghasilkan data orang lain. `EMPLOYEE` yang memanggilnya hanya
 * mendapatkan dirinya sendiri.
 */
export const fetchAttendanceDays = (
  auth: AuthState,
  from: string,
  to: string,
): Promise<readonly AttendanceDay[]> =>
  request<readonly AttendanceDay[]>(`/api/attendance?${attendanceRangeQuery(from, to)}`, auth);

export const checkIn = (
  auth: AuthState,
  idempotencyKey: string,
  body: CheckInBody,
): Promise<AttendanceActionResult> =>
  request<AttendanceActionResult>('/api/attendance/check-in', auth, {
    method: 'POST',
    body,
    idempotencyKey,
  });

export const checkOut = (
  auth: AuthState,
  idempotencyKey: string,
  body: CheckOutBody,
): Promise<AttendanceActionResult> =>
  request<AttendanceActionResult>('/api/attendance/check-out', auth, {
    method: 'POST',
    body,
    idempotencyKey,
  });

// ── Selfie absensi (item 9, review 12 Sep 2026) ─────────────────────────────

export const createSelfieUploadTarget = (
  auth: AuthState,
  body: CreateSelfieUploadTargetBody,
): Promise<UploadTargetResponse> =>
  request<UploadTargetResponse>('/api/attendance/selfies/upload-targets', auth, {
    method: 'POST',
    body,
  });

/** Membatalkan selfie SEBELUM finalisasi — dipanggil saat pengguna mengulang jepretan. */
export const cancelSelfieUpload = (auth: AuthState, fileId: string): Promise<void> =>
  request<void>(`/api/attendance/selfies/${encodeURIComponent(fileId)}`, auth, {
    method: 'DELETE',
  });

export const fetchSelfieSignedUrl = (auth: AuthState, fileId: string): Promise<SignedUrlResponse> =>
  request<SignedUrlResponse>(
    `/api/attendance/selfies/${encodeURIComponent(fileId)}/signed-url`,
    auth,
  );

/** Detail satu hari milik seorang pengguna — event mentah, bukan ringkasan. */
export const fetchAttendanceDay = (
  auth: AuthState,
  userId: string,
  workDate: string,
): Promise<{ readonly day: AttendanceDay | null; readonly events: readonly AttendanceEvent[] }> =>
  request(`/api/attendance/${encodeURIComponent(userId)}/${encodeURIComponent(workDate)}`, auth);

/**
 * Antrean koreksi. Scope-nya ditentukan SERVER dari peran pemanggil — karyawan melihat
 * miliknya sendiri, manajer melihat timnya. Klien tidak mengirim filter pengguna apa pun.
 */
export const fetchCorrections = (
  auth: AuthState,
  status?: 'PENDING' | 'APPROVED' | 'REJECTED',
): Promise<readonly AttendanceCorrection[]> =>
  request<readonly AttendanceCorrection[]>(
    status ? `/api/attendance/corrections?status=${status}` : '/api/attendance/corrections',
    auth,
  );

export const createCorrection = (
  auth: AuthState,
  body: CreateCorrectionBody,
): Promise<AttendanceCorrection> =>
  request<AttendanceCorrection>('/api/attendance/corrections', auth, { method: 'POST', body });

export const cancelCorrection = (auth: AuthState, id: string): Promise<AttendanceCorrection> =>
  request<AttendanceCorrection>(
    `/api/attendance/corrections/${encodeURIComponent(id)}/cancel`,
    auth,
    {
      method: 'POST',
      body: {},
    },
  );

export const approveCorrection = (
  auth: AuthState,
  id: string,
  body: DecideCorrectionBody,
): Promise<AttendanceCorrection> =>
  request<AttendanceCorrection>(
    `/api/attendance/corrections/${encodeURIComponent(id)}/approve`,
    auth,
    { method: 'POST', body },
  );

export const rejectCorrection = (
  auth: AuthState,
  id: string,
  body: DecideCorrectionBody,
): Promise<AttendanceCorrection> =>
  request<AttendanceCorrection>(
    `/api/attendance/corrections/${encodeURIComponent(id)}/reject`,
    auth,
    { method: 'POST', body },
  );

// ── Field Task (Gate D) ─────────────────────────────────────────────────────
//
// `Idempotency-Key` WAJIB pada seluruh transisi. Tanpa itu, tekan dua kali di jaringan lambat
// menghasilkan dua event untuk satu niat — dan riwayat task adalah append-only, jadi kesalahan
// itu tidak bisa dihapus, hanya bisa dijelaskan.

export const fetchMyTasks = (auth: AuthState, status?: string): Promise<readonly FieldTask[]> =>
  request<readonly FieldTask[]>(
    status ? `/api/field-tasks/me?status=${encodeURIComponent(status)}` : '/api/field-tasks/me',
    auth,
  );

export const fetchTasks = (auth: AuthState, status?: string): Promise<readonly FieldTask[]> =>
  request<readonly FieldTask[]>(
    status ? `/api/field-tasks?status=${encodeURIComponent(status)}` : '/api/field-tasks',
    auth,
  );

// Riwayat servis per alat (ADR-0054 Fase 2) — tiket yang externalRef-nya menunjuk alat ini.
export const fetchAssetTasks = (auth: AuthState, assetId: string): Promise<readonly FieldTask[]> =>
  request<readonly FieldTask[]>(`/api/field-tasks?assetId=${encodeURIComponent(assetId)}`, auth);

// ── Token agen telemetri (ADR-0055 Fase 3 slice 1) — izin workforce.asset.update ──
//
// Nilai token mentah hanya ada di respons issue/rotate (`IssuedAgentToken.token`) — SEKALI.
// `listAgentTokens` hanya mengembalikan metadata. Terbit/rotasi/cabut diaudit append-only di server.

export const listAgentTokens = (
  auth: AuthState,
  assetId: string,
): Promise<readonly AgentTokenView[]> =>
  request<readonly AgentTokenView[]>(
    `/api/assets/${encodeURIComponent(assetId)}/agent-token`,
    auth,
  );

export const issueAgentToken = (auth: AuthState, assetId: string): Promise<IssuedAgentToken> =>
  request<IssuedAgentToken>(`/api/assets/${encodeURIComponent(assetId)}/agent-token`, auth, {
    method: 'POST',
  });

export const rotateAgentToken = (auth: AuthState, assetId: string): Promise<IssuedAgentToken> =>
  request<IssuedAgentToken>(`/api/assets/${encodeURIComponent(assetId)}/agent-token/rotate`, auth, {
    method: 'POST',
  });

export const revokeAgentToken = (auth: AuthState, assetId: string): Promise<{ ok: true }> =>
  request<{ ok: true }>(`/api/assets/${encodeURIComponent(assetId)}/agent-token`, auth, {
    method: 'DELETE',
  });

// ── Telemetri (ADR-0055 Fase 3 slice 5) — izin workforce.telemetry.read ──────
export const fetchTelemetryLatest = (
  auth: AuthState,
  assetId: string,
): Promise<readonly TelemetryLatestMetric[]> =>
  request<readonly TelemetryLatestMetric[]>(
    `/api/assets/${encodeURIComponent(assetId)}/telemetry/latest`,
    auth,
  );

export const fetchTelemetryDaily = (
  auth: AuthState,
  assetId: string,
  metric: string,
  days?: number,
): Promise<readonly TelemetryDailyPoint[]> =>
  request<readonly TelemetryDailyPoint[]>(
    `/api/assets/${encodeURIComponent(assetId)}/telemetry/daily?metric=${encodeURIComponent(metric)}` +
      (days ? `&days=${days}` : ''),
    auth,
  );

export const fetchAlertEvents = (
  auth: AuthState,
  opts: { assetId?: string; open?: boolean } = {},
): Promise<readonly AlertEventResponse[]> => {
  const params = new URLSearchParams();
  if (opts.assetId) params.set('assetId', opts.assetId);
  if (opts.open) params.set('open', 'true');
  const qs = params.toString();
  return request<readonly AlertEventResponse[]>(`/api/alert-events${qs ? `?${qs}` : ''}`, auth);
};

// ── Aturan alert (ADR-0055 Fase 3 slice 4) — izin workforce.asset.update ─────
export const fetchAlertRules = (
  auth: AuthState,
  opts: { assetId?: string } = {},
): Promise<readonly AlertRuleResponse[]> => {
  const qs = opts.assetId ? `?assetId=${encodeURIComponent(opts.assetId)}` : '';
  return request<readonly AlertRuleResponse[]>(`/api/alert-rules${qs}`, auth);
};

export const createAlertRule = (
  auth: AuthState,
  body: CreateAlertRuleBody,
): Promise<AlertRuleResponse> =>
  request<AlertRuleResponse>('/api/alert-rules', auth, { method: 'POST', body });

export const updateAlertRule = (
  auth: AuthState,
  id: string,
  body: UpdateAlertRuleBody,
): Promise<AlertRuleResponse> =>
  request<AlertRuleResponse>(`/api/alert-rules/${encodeURIComponent(id)}`, auth, {
    method: 'PATCH',
    body,
  });

export const fetchTask = (auth: AuthState, id: string): Promise<FieldTask> =>
  request<FieldTask>(`/api/field-tasks/${encodeURIComponent(id)}`, auth);

export const fetchTaskEvents = (auth: AuthState, id: string): Promise<readonly FieldTaskEvent[]> =>
  request<readonly FieldTaskEvent[]>(`/api/field-tasks/${encodeURIComponent(id)}/events`, auth);

export const fetchTaskTypes = (auth: AuthState): Promise<readonly TaskTypeResponse[]> =>
  request<readonly TaskTypeResponse[]>('/api/task-types', auth);

/**
 * Surat jalan ERP yang boleh dipilih manajer — lewat `workforce-api`, BUKAN langsung ke ERP.
 *
 * Token ERP hidup di server dan tidak pernah sampai ke browser; kalau web memanggil ERP langsung,
 * kredensialnya ikut terkirim ke ponsel setiap kurir. Lihat `ERP-SHIPMENT-PULL-CONTRACT.md` §3,
 * dan penjaganya di `api/tanpa-token-erp.spec.ts`.
 */
export const fetchErpShipments = (auth: AuthState): Promise<readonly SuratJalanErp[]> =>
  request<readonly SuratJalanErp[]>('/api/erp/shipments', auth);

export const fetchErpShipment = (auth: AuthState, shipmentId: string): Promise<SuratJalanErp> =>
  request<SuratJalanErp>(`/api/erp/shipments/${encodeURIComponent(shipmentId)}`, auth);

export const createTask = (
  auth: AuthState,
  idempotencyKey: string,
  body: CreateFieldTaskBody,
): Promise<{ readonly task: FieldTask }> =>
  request<{ readonly task: FieldTask }>('/api/field-tasks', auth, {
    method: 'POST',
    body,
    idempotencyKey,
  });

const transition = <T>(
  auth: AuthState,
  id: string,
  action: string,
  idempotencyKey: string,
  body: unknown = {},
): Promise<T> =>
  request<T>(`/api/field-tasks/${encodeURIComponent(id)}/${action}`, auth, {
    method: 'POST',
    body,
    idempotencyKey,
  });

export const assignTask = (
  auth: AuthState,
  id: string,
  idempotencyKey: string,
  assigneeUserId: string,
): Promise<{ readonly task: FieldTask }> =>
  /*
   * HANYA `assigneeUserId`. `accessToken` DIHAPUS dari body.
   *
   * `ValidationPipe` API memakai `forbidNonWhitelisted: true`, jadi field yang tidak dideklarasikan
   * `AssignFieldTaskDto` membuat seluruh permintaan ditolak 400 — dan DTO itu hanya punya satu
   * field. Token yang dipakai server diambil dari header `Authorization`
   * (`accessToken: bearerToken(authorization)` di controller), bukan dari body; yang di body murni
   * berlebih dan tidak pernah dibaca siapa pun.
   *
   * Akibatnya PENUGASAN DARI WEB TIDAK PERNAH BERHASIL. Itu tak terlihat berbulan-bulan karena
   * layar penugasan menuntut UUID diketik tangan, sehingga tak seorang pun pernah sampai ke
   * panggilan ini (terlihat 26 Agustus 2026, beberapa menit setelah layarnya dibuat bisa dipakai).
   */
  transition(auth, id, 'assign', idempotencyKey, { assigneeUserId });

export const acceptTask = (
  auth: AuthState,
  id: string,
  idempotencyKey: string,
): Promise<{ readonly task: FieldTask }> => transition(auth, id, 'accept', idempotencyKey);

export const startTask = (
  auth: AuthState,
  id: string,
  idempotencyKey: string,
  body: TaskLocationBody,
): Promise<{ readonly task: FieldTask }> => transition(auth, id, 'start', idempotencyKey, body);

export const arriveTask = (
  auth: AuthState,
  id: string,
  idempotencyKey: string,
  body: TaskLocationBody,
): Promise<{ readonly task: FieldTask }> => transition(auth, id, 'arrive', idempotencyKey, body);

export const cancelTask = (
  auth: AuthState,
  id: string,
  idempotencyKey: string,
  note?: string | null,
): Promise<{ readonly task: FieldTask }> =>
  transition(auth, id, 'cancel', idempotencyKey, { note: note ?? null });

// ── Courier Delivery (Gate E) ───────────────────────────────────────────────

export const fetchDelivery = (auth: AuthState, taskId: string): Promise<DeliveryDetail | null> =>
  request<DeliveryDetail | null>(`/api/field-tasks/${encodeURIComponent(taskId)}/delivery`, auth);

export const fetchAttempts = (
  auth: AuthState,
  taskId: string,
): Promise<readonly DeliveryAttempt[]> =>
  request<readonly DeliveryAttempt[]>(
    `/api/field-tasks/${encodeURIComponent(taskId)}/attempts`,
    auth,
  );

export const fetchFailureReasonCodes = (auth: AuthState): Promise<readonly FailureReasonCode[]> =>
  request<readonly FailureReasonCode[]>('/api/failure-reason-codes', auth);

export const failTask = (
  auth: AuthState,
  taskId: string,
  idempotencyKey: string,
  body: FailTaskBody,
): Promise<{ readonly task: FieldTask }> =>
  request<{ readonly task: FieldTask }>(
    `/api/field-tasks/${encodeURIComponent(taskId)}/fail`,
    auth,
    {
      method: 'POST',
      body,
      idempotencyKey,
    },
  );

// ── Proof of Delivery (Gate E, ADR-0037) ────────────────────────────────────

export const createUploadTarget = (
  auth: AuthState,
  body: CreateUploadTargetBody,
): Promise<UploadTargetResponse> =>
  request<UploadTargetResponse>('/api/files/upload-targets', auth, { method: 'POST', body });

export const fetchSignedUrl = (auth: AuthState, fileId: string): Promise<SignedUrlResponse> =>
  request<SignedUrlResponse>(`/api/files/${encodeURIComponent(fileId)}/signed-url`, auth);

export const completeTask = (
  auth: AuthState,
  taskId: string,
  idempotencyKey: string,
  body: CompleteTaskBody,
): Promise<{ readonly task: FieldTask }> =>
  request<{ readonly task: FieldTask }>(
    `/api/field-tasks/${encodeURIComponent(taskId)}/complete`,
    auth,
    { method: 'POST', body, idempotencyKey },
  );

/**
 * `PUT` langsung ke Supabase Storage — SENGAJA di luar `request()`.
 *
 * `uploadUrl` bukan endpoint kita: ia tidak memakai amplop respons, tidak menerima header
 * autentikasi HBS, dan mengirimkannya ke sana justru membocorkan token ke pihak ketiga.
 * Progres dilaporkan lewat XHR karena `fetch` tidak menyediakan progres unggah.
 */
export function putToUploadUrl(
  uploadUrl: string,
  blob: Blob,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('content-type', blob.type);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Unggah gagal (HTTP ${xhr.status}).`));
    xhr.onerror = () => reject(new Error('Jaringan terputus saat mengunggah.'));
    xhr.onabort = () => reject(new Error('Unggah dibatalkan.'));
    xhr.send(blob);
  });
}

// ── Dashboard (Issue #467) ──────────────────────────────────────────────────

export const fetchAttendanceSummary = (auth: AuthState, date: string): Promise<AttendanceSummary> =>
  request<AttendanceSummary>(`/api/attendance/summary?date=${encodeURIComponent(date)}`, auth);

export const fetchAttendanceSummaryRange = (
  auth: AuthState,
  from: string,
  to: string,
): Promise<readonly AttendanceSummary[]> =>
  request<readonly AttendanceSummary[]>(
    `/api/attendance/summary/range?${attendanceRangeQuery(from, to)}`,
    auth,
  );

export const fetchTaskSummary = (auth: AuthState): Promise<TaskSummary> =>
  request<TaskSummary>('/api/field-tasks/summary', auth);

/**
 * Mengunduh ekspor CSV memakai path yang SAMA dengan daftar di layar.
 *
 * Tidak lewat `request()`: responsnya `text/csv` ber-BOM, bukan amplop JSON, dan
 * `@SkipResponseEnvelope` di server memang membuatnya demikian. Berkas diambil sebagai blob
 * lalu diunduh lewat object URL supaya header autentikasi tetap terkirim — `window.open` ke
 * URL API akan gagal 401 karena tidak membawa token.
 */
export async function downloadTaskExport(
  auth: AuthState,
  exportPath: string,
  fileName: string,
): Promise<void> {
  const response = await fetch(`${config.apiBaseUrl}${exportPath}`, {
    method: 'GET',
    headers: buildHeaders(auth, { hasBody: false }),
  });
  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // Bukan JSON — `toApiError` menyiapkan pesan bawaan.
    }
    throw toApiError(response.status, body, response.headers.get('x-correlation-id'));
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  // Melepas object URL — tanpa ini blob berkas besar tertahan sampai tab ditutup.
  URL.revokeObjectURL(url);
}

/* ── Lokasi kerja ────────────────────────────────────────────────────────── */

export interface CreateWorkLocationBody {
  readonly name: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly geofenceRadiusMeters: number;
}

/**
 * Mengaktifkan kebijakan absensi organisasi.
 *
 * `POST`, bukan `PUT`: server memperlakukan ini sebagai MENGAKTIFKAN kebijakan baru, bukan
 * menyunting baris yang sama di tempat — riwayat kebijakan lama tetap ada dan dapat diaudit.
 * Badannya dibangun `bodiKebijakan()`; jangan menyusunnya di tempat lain, karena satu field asing
 * membatalkan seluruh permintaan.
 */
/**
 * Membuat jenis tugas. `requiresProof` TIDAK dikirim — server menurunkannya dari `category`.
 *
 * Mengirimnya bukan hanya sia-sia, melainkan DITOLAK: `forbidNonWhitelisted` membatalkan seluruh
 * permintaan. Itu disengaja — pengirim perlu tahu ia tidak lagi memutuskan sifat bukti.
 */
export const createTaskType = (
  auth: AuthState,
  body: { code: string; name: string; category: string },
): Promise<TaskTypeResponse> =>
  request<TaskTypeResponse>('/api/task-types', auth, { method: 'POST', body });

export const disableTaskType = (auth: AuthState, id: string): Promise<TaskTypeResponse> =>
  request<TaskTypeResponse>(`/api/task-types/${encodeURIComponent(id)}/disable`, auth, {
    method: 'POST',
  });

export const createFailureReasonCode = (
  auth: AuthState,
  body: { code: string; label: string; category: string; requiresDetail?: boolean },
): Promise<FailureReasonCode> =>
  request<FailureReasonCode>('/api/failure-reason-codes', auth, { method: 'POST', body });

export const disableFailureReasonCode = (auth: AuthState, id: string): Promise<FailureReasonCode> =>
  request<FailureReasonCode>(`/api/failure-reason-codes/${encodeURIComponent(id)}/disable`, auth, {
    method: 'POST',
  });

export const activateAttendancePolicy = (
  auth: AuthState,
  body: ReturnType<typeof import('../lib/kebijakan-absensi').bodiKebijakan>,
): Promise<AttendancePolicyResponse> =>
  request<AttendancePolicyResponse>('/api/attendance-policies', auth, { method: 'POST', body });

export const createWorkLocation = (
  auth: AuthState,
  body: CreateWorkLocationBody,
): Promise<WorkLocationResponse> =>
  request<WorkLocationResponse>('/api/work-locations', auth, { method: 'POST', body });

export const disableWorkLocation = (auth: AuthState, id: string): Promise<WorkLocationResponse> =>
  request<WorkLocationResponse>(`/api/work-locations/${encodeURIComponent(id)}/disable`, auth, {
    method: 'POST',
  });

/**
 * Pencarian nama tempat lewat Nominatim (OpenStreetMap).
 *
 * TIDAK lewat Workforce API — ini layanan publik pihak ketiga, dan memproksikannya lewat server
 * kita hanya menambah satu hop tanpa menambah keamanan apa pun. Tidak ada data organisasi yang
 * ikut; yang dikirim hanya kata yang diketik admin.
 *
 * Kegagalan diserahkan ke pemanggil sebagai penolakan biasa: pencarian ini kenyamanan, bukan
 * syarat. Admin tetap dapat menunjuk titik langsung di peta.
 */
export const searchPlaces = async (query: string): Promise<readonly PlaceSuggestion[]> => {
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(query)}`;
  const response = await fetch(url, { headers: { accept: 'application/json' } });
  if (!response.ok) throw new Error(`Nominatim status ${response.status}`);
  return parsePlaceSuggestions(await response.json());
};
/* ── Kelola Karyawan ─────────────────────────────────────────────────────── */

/**
 * Anggota organisasi yang dapat dijadikan pekerja.
 *
 * Datang dari HBS lewat Workforce API (`GET /api/members`), bukan dibaca langsung: Workforce
 * tidak boleh menyentuh tabel keanggotaan HBS (ADR-0033 A2).
 *
 * Tanpa email dan nomor telepon — itu keputusan HBS. Layar ini hanya perlu tahu siapa yang boleh
 * dipilih, bukan cara menghubunginya.
 */
export const fetchMembers = (auth: AuthState): Promise<readonly Member[]> =>
  request<readonly Member[]>('/api/members', auth);

export const fetchWorkers = (auth: AuthState): Promise<readonly WorkerProfile[]> =>
  request<readonly WorkerProfile[]>('/api/workers', auth);

export interface CreateWorkerBody {
  readonly userId: string;
  readonly employeeNumber?: string;
  readonly jobTitle?: string;
  readonly department?: string;
}

export const createWorker = (auth: AuthState, body: CreateWorkerBody): Promise<WorkerProfile> =>
  request<WorkerProfile>('/api/workers', auth, { method: 'POST', body });

/**
 * Peran Workforce yang berlaku di organisasi ini.
 *
 * Dipisah dari daftar pekerja karena keduanya memang terpisah di server: profil pekerja membuat
 * absensi seseorang terlihat di dashboard, peran membuat ia dapat MASUK sama sekali. Sebelum ada
 * pemanggil ini, satu-satunya cara memberi peran adalah `--admin` pada skrip provisioning (yang
 * hanya memberi WORKFORCE_ADMIN) atau memanggil endpoint ini dengan tangan.
 */
export const fetchRoleAssignments = (auth: AuthState): Promise<readonly RoleAssignment[]> =>
  request<readonly RoleAssignment[]>('/api/roles/assignments', auth);

export const assignRole = (
  auth: AuthState,
  body: { readonly userId: string; readonly role: WorkforceRole },
): Promise<RoleAssignment> =>
  request<RoleAssignment>('/api/roles/assignments', auth, { method: 'POST', body });

/**
 * Tim — penentu apa yang DILIHAT seorang Manajer.
 *
 * Cakupan `MANAGER` adalah `team`, dan penyelesaiannya berbunyi "dirinya + anggota tim yang ia
 * kelola". Tanpa tim, seorang Manajer hanya melihat dirinya sendiri — jadi tim bukan kerapian
 * organisasi, melainkan sakelar yang menentukan apakah perannya berarti apa-apa.
 *
 * Ditemukan 23 Agustus 2026: endpoint-endpoint ini sudah ada sejak lama, tetapi TIDAK ADA satu
 * layar pun yang memanggilnya, dan basis data produksi berisi NOL tim.
 */
export interface Team {
  readonly id: string;
  readonly name: string;
  readonly managerUserId: string;
  /** Diisi server sejak 23 Agu 2026; layar memakainya untuk menampilkan isi tim. */
  readonly memberUserIds: readonly string[];
}

export const fetchTeams = (auth: AuthState): Promise<readonly Team[]> =>
  request<readonly Team[]>('/api/teams', auth);

export const createTeam = (
  auth: AuthState,
  body: { readonly name: string; readonly managerUserId: string },
): Promise<Team> => request<Team>('/api/teams', auth, { method: 'POST', body });

/**
 * Ubah nama tim.
 *
 * SENGAJA tidak menerima `managerUserId`: memindahkan tim ke manajer lain mengubah siapa melihat
 * siapa, dan itu tidak boleh terjadi lewat pintu yang namanya hanya menyebut "ubah nama".
 */
export const renameTeam = (auth: AuthState, teamId: string, name: string): Promise<Team> =>
  request<Team>(`/api/teams/${encodeURIComponent(teamId)}`, auth, {
    method: 'PATCH',
    body: { name },
  });

export const addTeamMember = (auth: AuthState, teamId: string, userId: string): Promise<unknown> =>
  request<unknown>(`/api/teams/${encodeURIComponent(teamId)}/members`, auth, {
    method: 'POST',
    body: { userId },
  });

export const removeTeamMember = (
  auth: AuthState,
  teamId: string,
  userId: string,
): Promise<unknown> =>
  request<unknown>(
    `/api/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
    auth,
    { method: 'DELETE' },
  );

export interface UpdateWorkerBody {
  readonly employeeNumber?: string;
  readonly jobTitle?: string;
  readonly department?: string;
  readonly employmentStatus?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
}

export const updateWorker = (
  auth: AuthState,
  id: string,
  body: UpdateWorkerBody,
): Promise<WorkerProfile> =>
  request<WorkerProfile>(`/api/workers/${encodeURIComponent(id)}`, auth, {
    method: 'PATCH',
    body,
  });

// ── Dokumen penagihan pada tugas ("tukar faktur") ───────────────────────────

/**
 * Server mengirim `{ documents, state }`, bukan array telanjang. Dinormalkan di sini supaya
 * bentuk yang meleset tidak pernah lagi sampai ke komponen sebagai objek yang di-`.filter`.
 */
export const fetchTaskDocuments = (
  auth: AuthState,
  taskId: string,
): Promise<readonly TaskDocument[]> =>
  request<unknown>(`/api/field-tasks/${encodeURIComponent(taskId)}/documents`, auth).then(
    parseTaskDocuments,
  );

export const setTaskDocuments = (
  auth: AuthState,
  taskId: string,
  idempotencyKey: string,
  documents: readonly { readonly documentType: DocumentType; readonly mustReturn: boolean }[],
): Promise<readonly TaskDocument[]> =>
  request<readonly TaskDocument[]>(
    `/api/field-tasks/${encodeURIComponent(taskId)}/documents`,
    auth,
    { method: 'POST', body: { documents }, idempotencyKey },
  );

/**
 * Menandai satu berkas. `carried` lalu `returned` — urutannya ditegakkan server (`TASK-441`),
 * bukan disimpulkan dari keadaan layar yang bisa saja basi.
 */
const markDocument = (
  auth: AuthState,
  taskId: string,
  documentId: string,
  step: 'carried' | 'returned',
  idempotencyKey: string,
): Promise<TaskDocument> =>
  request<TaskDocument>(
    `/api/field-tasks/${encodeURIComponent(taskId)}/documents/${encodeURIComponent(documentId)}/${step}`,
    auth,
    { method: 'POST', body: {}, idempotencyKey },
  );

export const markDocumentCarried = (
  auth: AuthState,
  taskId: string,
  documentId: string,
  idempotencyKey: string,
): Promise<TaskDocument> => markDocument(auth, taskId, documentId, 'carried', idempotencyKey);

export const markDocumentReturned = (
  auth: AuthState,
  taskId: string,
  documentId: string,
  idempotencyKey: string,
): Promise<TaskDocument> => markDocument(auth, taskId, documentId, 'returned', idempotencyKey);

/**
 * Menghapus tugas yang salah dimasukkan — beserta seluruh barisan di bawahnya, di server.
 *
 * `DELETE` sungguhan, bukan `POST :id/delete`: bentuk kata kerjanya membawa arti, dan alat mana
 * pun yang membaca lalu lintas ini langsung tahu bahwa panggilan ini merusak.
 */
export const deleteTask = (auth: AuthState, id: string): Promise<{ readonly deleted: number }> =>
  request<{ readonly deleted: number }>(`/api/field-tasks/${encodeURIComponent(id)}`, auth, {
    method: 'DELETE',
  });

/**
 * Titik terakhir tiap orang dalam cakupan pemanggil — nama lokasi dan waktu, BUKAN koordinat.
 *
 * Server sengaja tidak mengirim lintang-bujur (ADR-0036 prinsip 1, minimisasi): manajer yang
 * memilih kurir tidak membutuhkannya, dan tidak mengirimkannya berarti presisi itu tidak pernah
 * meninggalkan lapisan yang menyimpannya.
 */
export const fetchLastSeen = (auth: AuthState): Promise<readonly TerakhirTerlihat[]> =>
  request<readonly TerakhirTerlihat[]>('/api/field-tasks/last-seen', auth);

/* ── Web Push — lihat `lib/webPush.ts` ──────────────────────────────────── */

export interface DeviceTokenBody {
  readonly token: string;
  readonly platform?: 'web';
  readonly deviceName?: string;
}

/** `publicKey: null` berarti push belum aktif/dikonfigurasi di server — bukan galat. */
export const fetchVapidPublicKey = (auth: AuthState): Promise<{ publicKey: string | null }> =>
  request<{ publicKey: string | null }>('/api/notifications/device-tokens/vapid-public-key', auth);

export const registerDeviceToken = (
  auth: AuthState,
  body: DeviceTokenBody,
): Promise<{ ok: true }> =>
  request<{ ok: true }>('/api/notifications/device-tokens', auth, { method: 'POST', body });

export const unregisterDeviceToken = (
  auth: AuthState,
  body: { readonly token: string },
): Promise<{ ok: true }> =>
  request<{ ok: true }>('/api/notifications/device-tokens', auth, { method: 'DELETE', body });
